import { Button } from "@/components/ui/button";
import { Eye, Video, Square, StopCircle, PlayCircle, Activity } from "lucide-react";
import { useState } from "react";
import { api } from "@/lib/api";
import { ConfirmButton, timeAgo } from "./AdminShared";
import VodPlayerModal from "@/components/VodPlayerModal";
import LiveDiagnosticsModal from "./LiveDiagnosticsModal";

const VOD_STATUS_LABEL = {
  pending: { text: "Recording…", cls: "text-rose-400" },
  in_progress: { text: "Finalizing…", cls: "text-amber-400" },
  ready: { text: "VOD ready", cls: "text-emerald-400" },
  partial: { text: "VOD partial", cls: "text-amber-400" },
  failed: { text: "VOD failed", cls: "text-rose-500" },
  aborted: { text: "VOD aborted", cls: "text-rose-500" },
};

export default function LiveTab({ streams, onGhost, onStartRec, onStopRec, onForceEnd, hideGhost = false }) {
  const [openVodStreamId, setOpenVodStreamId] = useState(null);
  const [diag, setDiag] = useState({ open: false, loading: false, data: null, error: null });
  const runDiagnostics = async (s) => {
    setDiag({ open: true, loading: true, data: null, error: null });
    try {
      const { data } = await api.get(`/admin/live/${s.id}/diagnostics`);
      setDiag({ open: true, loading: false, data, error: null });
    } catch (e) {
      setDiag({ open: true, loading: false, data: null, error: e?.response?.data?.detail || "Diagnostics failed." });
    }
  };
  if (streams.length === 0) {
    return <div className="card-surface p-8 text-center text-sm text-zinc-500">No streams.</div>;
  }
  return (
    <div className="space-y-2">
      {streams.map((s) => {
        const recStatus = s.recording?.status;
        const label = recStatus ? VOD_STATUS_LABEL[recStatus] : null;
        const canPlayVod = recStatus === "ready" || recStatus === "partial";
        return (
          <div key={s.id} data-testid={`admin-live-${s.id}`} className="card-surface p-4 flex items-center gap-4 flex-wrap">
            <div className={`w-2 h-2 rounded-full ${s.is_live ? "bg-rose-600 pulse-dot" : "bg-zinc-600"}`} />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{s.title || "Untitled Live"}</div>
              <div className="text-xs text-zinc-500 truncate">
                {s.host?.display_name} · {s.viewer_count} viewers · started {timeAgo(s.started_at)} ago
                {!s.is_live && " · ended"}
                {label && (
                  <> · <span data-testid={`admin-live-${s.id}-vod-status`} className={label.cls}>{label.text}</span></>
                )}
              </div>
            </div>
          <Button
            size="sm"
            onClick={() => runDiagnostics(s)}
            className="rounded-none text-xs bg-transparent border border-cyan-700/60 text-cyan-300 hover:border-cyan-400"
            data-testid={`admin-live-${s.id}-diagnose`}
          >
            <Activity size={13} /> Diagnose
          </Button>
          {s.is_live && (
            <>
              {!hideGhost && (
              <Button
                size="sm"
                onClick={() => onGhost(s)}
                className="rounded-none text-xs bg-transparent border border-white/15 text-white hover:border-rose-600"
                data-testid={`admin-live-${s.id}-ghost`}
              >
                <Eye size={13} /> Ghost view
              </Button>
              )}
              {s.recording?.egress_id && !s.recording?.stopped_at ? (
                <ConfirmButton
                  testid={`admin-live-${s.id}-recstop`}
                  variant="danger"
                  label="Stop recording?"
                  description="This will end the current room-composite egress. The MP4 will be finalized in your LiveKit Cloud dashboard."
                  onConfirm={() => onStopRec(s)}
                >
                  <Square size={13} /> Stop rec
                </ConfirmButton>
              ) : (
                <ConfirmButton
                  testid={`admin-live-${s.id}-recstart`}
                  label="Start recording?"
                  description="Records the room composite (grid layout) to your LiveKit Cloud egress destination. Configure a destination in LiveKit Cloud → Settings → Egress before starting."
                  onConfirm={() => onStartRec(s)}
                >
                  <Video size={13} /> Record
                </ConfirmButton>
              )}
              <ConfirmButton
                testid={`admin-live-${s.id}-end`}
                variant="danger"
                label="Force-end this stream?"
                description="The host will be disconnected immediately."
                onConfirm={() => onForceEnd(s.id)}
              >
                <StopCircle size={13} /> End
              </ConfirmButton>
            </>
          )}
          {canPlayVod && (
            <Button
              size="sm"
              onClick={() => setOpenVodStreamId(s.id)}
              className="rounded-none text-xs bg-emerald-700 hover:bg-emerald-600 text-white"
              data-testid={`admin-live-${s.id}-vod-view`}
            >
              <PlayCircle size={13} /> View recording
            </Button>
          )}
          </div>
        );
      })}
      <VodPlayerModal
        open={!!openVodStreamId}
        onOpenChange={(v) => !v && setOpenVodStreamId(null)}
        streamId={openVodStreamId}
      />
      <LiveDiagnosticsModal
        open={diag.open}
        onClose={() => setDiag({ open: false, loading: false, data: null, error: null })}
        loading={diag.loading}
        data={diag.data}
        error={diag.error}
      />
    </div>
  );
}
